import * as React from 'react';
import { Dispatch } from 'react-redux';
import { ContextualMenuItemType } from '../../constants';
import { StoreState } from '../../../../reducers';
import { DriveItemInfo } from '../../../../containers/DriveItemList/reducer';
import { StyleColor, NewStyleColor } from '../../../../utils/styles-constants';
import { closeContextualMenu, deleteFromAsp } from '../../../../containers/DriveItemList/actions';
import { deleteItem, requestWebDownloadItem, requestMobileAppDownload } from '../../../../containers/DownloadManager/actions';
import UserAgentUtils from '../../../../utils/userAgent';
import StatsUtils from '../../../../utils/statistics';
import { DownloadMode } from '../../../../containers/DownloadManager/reducer';
import ContextualMenuItem, { ContextualMenuItemProps } from '..';
import { share } from '../../../../containers/Share/actions';
import RoutesUtils from '../../../../utils/routes';
import { openProposalCreatorForSaveAs, setAsTemplate, removeTemplate } from '../../../../containers/ProposalCreator/actions';
import { openAssetRenamePanel } from '../../../../containers/AssetRenamePanel/actions';
import { openNoSaveAsPopup } from '../../../../containers/NoSaveAsPopup/actions';

interface ContextualMenuItemContainerProps {
	dispatch: Dispatch<StoreState>;
	driveItem: DriveItemInfo;
	type: ContextualMenuItemType;
	clientId: number;
	isTemplate: boolean;
}

class ContextualMenuItemContainer extends React.Component<ContextualMenuItemContainerProps, never> {
	constructor(props: ContextualMenuItemContainerProps) {
		super(props);
	}

	close() {
		const { dispatch } = this.props;
		dispatch(closeContextualMenu());
	}

	onDownloadClick(isUpdate: boolean) {
		const { driveItem, dispatch } = this.props;
		const { isMobileApp } = UserAgentUtils;

		if (driveItem) {
			if (!isUpdate) {
				StatsUtils.trackDownload([driveItem]);
			}

			if (!isMobileApp) {
				dispatch(requestWebDownloadItem(driveItem));
			} else {
				dispatch(requestMobileAppDownload(driveItem, DownloadMode.Requested));
			}
		}
		this.close();
	}

	onDeleteClick() {
		const { driveItem, dispatch } = this.props;

		dispatch(deleteItem(driveItem));
		this.close();
	}

	onShareClick() {
		const { dispatch, driveItem } = this.props;

		dispatch(share([driveItem]));
		this.close();
	}

	onEditClick() {
		const { clientId, driveItem: { id } } = this.props;
		RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, id));
		this.close();
	}

	onProposalCopy() {
		const { driveItem, dispatch } = this.props;
		dispatch(openProposalCreatorForSaveAs(driveItem));
		this.close();
	}

	onProposalNoSaveAs() {
		const { dispatch } = this.props;
		dispatch(openNoSaveAsPopup());
		this.close();
	}

	onRename() {
		const { driveItem, dispatch } = this.props;
		dispatch(openAssetRenamePanel(driveItem));
		this.close();
	}

	onDeleteFromAspClick() {
		const { driveItem, clientId, dispatch } = this.props;

		dispatch(deleteFromAsp(clientId, driveItem));
		this.close();
	}

	onSetAsTemplateClick() {
		const { driveItem: { id }, clientId, dispatch } = this.props;

		dispatch(setAsTemplate(clientId, id));
		this.close();
	}

	onRemoveTemplateClick() {
		const { driveItem: { id }, clientId, dispatch } = this.props;

		dispatch(removeTemplate(clientId, id));
		this.close();
	}

	getItemAttributes(type: ContextualMenuItemType): ContextualMenuItemProps {
		const defaultSize = 20;
		switch (type) {
			case ContextualMenuItemType.Download:
				return {
					label: UserAgentUtils.isMobileApp ? 'messages.activate_offline_mode' : 'messages.download',
					iconName: UserAgentUtils.isMobileApp ? 'iconset_uprez_check_circle' : 'iconset_uprez_download_bold',
					color: NewStyleColor.Black,
					size: defaultSize + 4,
					onClick: () => this.onDownloadClick(false),
				};
			case ContextualMenuItemType.Update:
				return {
					label: 'messages.update',
					iconName: 'iconset_update',
					color: StyleColor.Blue600,
					size: defaultSize,
					onClick: () => this.onDownloadClick(true),
				};
			case ContextualMenuItemType.Delete:
				return {
					label: 'messages.available_offline',
					iconName: 'iconset_uprez_check_circle_green',
					color: NewStyleColor.VibrantGreen,
					size: defaultSize + 4,
					onClick: () => this.onDeleteClick(),
				};
			case ContextualMenuItemType.Share:
				return {
					label: 'messages.share',
					iconName: 'iconset_uprez_share_bold',
					color: StyleColor.Black,
					size: defaultSize - 2,
					onClick: () => this.onShareClick(),
				};
			case ContextualMenuItemType.Edit:
				return {
					label: 'messages.edit',
					iconName: 'iconset_uprez_write',
					color: StyleColor.Black,
					size: defaultSize,
					onClick: () => this.onEditClick(),
				};
			case ContextualMenuItemType.ProposalSaveAs:
				return {
					label: 'messages.save_as',
					iconName: 'iconset_uprez_save_as',
					color: StyleColor.Black,
					size: defaultSize,
					onClick: () => this.onProposalCopy(),
				};
			case ContextualMenuItemType.ProposalNoSaveAs:
				return {
					label: 'messages.save_as',
					iconName: 'iconset_uprez_info',
					color: StyleColor.Red,
					size: defaultSize + 4,
					onClick: () => this.onProposalNoSaveAs(),
				};
			case ContextualMenuItemType.Rename:
				return {
					label: 'messages.rename',
					iconName: 'iconset_uprez_rename',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onRename(),
				};
			case ContextualMenuItemType.DeleteFromAsp:
				return {
					label: 'messages.delete',
					iconName: 'iconset_uprez_delete',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onDeleteFromAspClick(),
				};
			case ContextualMenuItemType.SetAsTemplate:
				const { isTemplate } = this.props;
				return {
					label: isTemplate ? 'messages.available_as_template' : 'messages.set_as_template',
					iconName: isTemplate ? 'iconset_uprez_check_circle_green' : 'iconset_uprez_set_as_template',
					color: isTemplate ? NewStyleColor.VibrantGreen : NewStyleColor.Black,
					size: isTemplate ? defaultSize + 4 : defaultSize,
					onClick: () => {
						if (isTemplate) {
							this.onRemoveTemplateClick();
						} else {
							this.onSetAsTemplateClick();
						}
					}
				};
			case ContextualMenuItemType.RemoveTemplate:
				return {
					label: 'messages.remove_template',
					iconName: 'iconset_uprez_delete',
					color: NewStyleColor.Black,
					size: defaultSize,
					onClick: () => this.onRemoveTemplateClick(),
				};
		}
	}

	render() {
		const { type } = this.props;

		const itemAttributes = this.getItemAttributes(type);
		return <ContextualMenuItem {...itemAttributes} />;
	}
}

export default ContextualMenuItemContainer;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ContextualMenu/Item/Container/index.tsx